import { useCallback, useRef, useState } from 'react';
import { Maximize, Minus, Plus, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ReviewImageViewerProps {
  src: string;
  alt: string;
}

interface Transform {
  x: number;
  y: number;
  scale: number;
  rotation: number;
}

const INITIAL: Transform = { x: 0, y: 0, scale: 1, rotation: 0 };
const MIN_SCALE = 0.1;
const MAX_SCALE = 12;
const WHEEL_ZOOM_FACTOR = 1.0015;

export function ReviewImageViewer({ src, alt }: ReviewImageViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [t, setT] = useState<Transform>(INITIAL);
  const panRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  const reset = useCallback(() => setT(INITIAL), []);

  const zoomBy = useCallback(
    (factor: number, originX?: number, originY?: number) => {
      setT((prev) => {
        const nextScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev.scale * factor));
        if (nextScale === prev.scale) return prev;
        if (originX == null || originY == null) {
          return { ...prev, scale: nextScale };
        }
        // Keep the point under the cursor stationary while zooming.
        const ratio = nextScale / prev.scale;
        return {
          ...prev,
          scale: nextScale,
          x: originX - (originX - prev.x) * ratio,
          y: originY - (originY - prev.y) * ratio,
        };
      });
    },
    [],
  );

  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      // The image's center sits at the canvas center (translate(-50%,-50%) on the <img>),
      // so zoomBy works in canvas-center-relative coords. We translate the cursor's
      // top-left coord into a center-relative offset so the point under the mouse stays
      // pinned during the zoom.
      const rect = e.currentTarget.getBoundingClientRect();
      const ox = e.clientX - rect.left - rect.width / 2;
      const oy = e.clientY - rect.top - rect.height / 2;
      const factor = WHEEL_ZOOM_FACTOR ** -e.deltaY;
      zoomBy(factor, ox, oy);
    },
    [zoomBy],
  );

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    panRef.current = { startX: e.clientX, startY: e.clientY, origX: t.x, origY: t.y };
  }, [t.x, t.y]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const pan = panRef.current;
    if (!pan) return;
    setT((prev) => ({
      ...prev,
      x: pan.origX + (e.clientX - pan.startX),
      y: pan.origY + (e.clientY - pan.startY),
    }));
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    panRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }, []);

  const handleDoubleClick = useCallback(() => reset(), [reset]);

  const fitToContainer = useCallback(() => {
    setT((prev) => ({ ...prev, x: 0, y: 0, scale: 1 }));
  }, []);

  const rotate = useCallback(() => {
    setT((prev) => ({ ...prev, rotation: (prev.rotation + 90) % 360 }));
  }, []);

  return (
    <div className="relative flex h-full w-full flex-col">
      <div className="absolute right-3 top-3 z-10 flex items-center gap-1 rounded-md border bg-background/90 p-1 shadow-sm backdrop-blur">
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => zoomBy(0.8)} title="Zoom out">
          <Minus className="h-3.5 w-3.5" />
        </Button>
        <span className="min-w-[3rem] text-center text-[11px] tabular-nums text-muted-foreground">
          {Math.round(t.scale * 100)}%
        </span>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => zoomBy(1.25)} title="Zoom in">
          <Plus className="h-3.5 w-3.5" />
        </Button>
        <div className="mx-1 h-4 w-px bg-border" />
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={fitToContainer} title="Fit">
          <Maximize className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={rotate} title="Rotate 90°">
          <RotateCw className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={reset} title="Reset">
          Reset
        </Button>
      </div>

      <div
        ref={containerRef}
        className="relative h-full w-full cursor-grab touch-none select-none overflow-hidden bg-muted/30 active:cursor-grabbing"
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={handleDoubleClick}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="absolute left-1/2 top-1/2 max-h-none max-w-none origin-center select-none"
          style={{
            transform: `translate(-50%, -50%) translate(${t.x}px, ${t.y}px) scale(${t.scale}) rotate(${t.rotation}deg)`,
            transition: panRef.current ? 'none' : 'transform 0.05s linear',
          }}
        />
      </div>
    </div>
  );
}
